import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  TextField,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  InputAdornment,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Snackbar,
} from '@mui/material';
import {
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkOutlineIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Star as StarIcon,
  StarBorder as StarOutlineIcon,
  AccessTime as TimeIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  AttachFile as AttachIcon,
  Add as AddIcon,
  OpenInNew as OpenIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  Sort as SortIcon,
  ViewList as ListViewIcon,
  ViewModule as GridViewIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../utils/api';

interface Bookmark {
  id: string;
  title: string;
  description?: string;
  type: 'email' | 'link' | 'attachment' | 'note';
  url?: string;
  emailId?: string;
  folder: string;
  tags: string[];
  isStarred: boolean;
  createdAt: Date;
  from?: string;
  subject?: string;
}

interface BookmarkFolder {
  id: string;
  name: string;
  color: string;
  count: number;
}

// Map API response to frontend model
const mapBookmark = (b: any): Bookmark => ({
  id: b._id || b.id,
  title: b.title || '',
  description: b.description || '',
  type: b.type || 'link',
  url: b.url,
  emailId: b.emailId,
  folder: b.folder || 'all',
  tags: b.tags || [],
  isStarred: b.isStarred || false,
  createdAt: new Date(b.createdAt),
  from: b.from,
  subject: b.subject,
});

const mapFolder = (f: any): BookmarkFolder => ({
  id: f._id || f.id || f.name,
  name: f.name,
  color: f.color || '#1976d2',
  count: f.count || 0,
});

const BookmarksPage: React.FC = () => {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [folders, setFolders] = useState<BookmarkFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'type'>('date');
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<{ anchor: HTMLElement; bookmarkId: string } | null>(null);
  const [showAddBookmark, setShowAddBookmark] = useState(false);
  const [newBookmark, setNewBookmark] = useState({ title: '', url: '', description: '', folder: 'all' });
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editBookmark, setEditBookmark] = useState<Bookmark | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  const fetchBookmarks = useCallback(async () => {
    try {
      const params: any = {};
      if (selectedFolder !== 'all') params.folder = selectedFolder;
      if (searchQuery) params.search = searchQuery;
      const res = await api.get('/bookmarks', { params });
      const data = res.data?.data || res.data || [];
      setBookmarks(Array.isArray(data) ? data.map(mapBookmark) : []);
    } catch { /* ignore */ }
  }, [selectedFolder, searchQuery]);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await api.get('/bookmarks/folders');
      const data = res.data?.data || res.data || [];
      setFolders(Array.isArray(data) ? data.map(mapFolder) : []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  useEffect(() => {
    fetchBookmarks();
  }, [fetchBookmarks]);

  // Listen for global refresh event from header
  useEffect(() => {
    const handleRefresh = () => { fetchFolders(); fetchBookmarks(); };
    window.addEventListener('app-refresh', handleRefresh);
    return () => window.removeEventListener('app-refresh', handleRefresh);
  }, [fetchFolders, fetchBookmarks]);

  const handleToggleStar = async (bookmarkId: string) => {
    try {
      const res = await api.put(`/bookmarks/${bookmarkId}/star`);
      const updated = mapBookmark(res.data?.data || res.data);
      setBookmarks(bookmarks.map(b => b.id === bookmarkId ? updated : b));
    } catch { /* ignore */ }
  };

  const handleDeleteBookmark = async (bookmarkId: string) => {
    try {
      await api.delete(`/bookmarks/${bookmarkId}`);
      setBookmarks(bookmarks.filter(b => b.id !== bookmarkId));
    } catch { /* ignore */ }
    setMoreMenuAnchor(null);
  };

  const handleAddBookmark = async () => {
    if (!newBookmark.title.trim()) return;

    try {
      const dto = {
        title: newBookmark.title,
        description: newBookmark.description || undefined,
        type: newBookmark.url ? 'link' : 'note',
        url: newBookmark.url || undefined,
        folder: newBookmark.folder !== 'all' ? newBookmark.folder : undefined,
      };
      const res = await api.post('/bookmarks', dto);
      const created = mapBookmark(res.data?.data || res.data);
      setBookmarks([created, ...bookmarks]);
      setShowAddBookmark(false);
      setNewBookmark({ title: '', url: '', description: '', folder: 'all' });
      fetchFolders();
    } catch { /* ignore */ }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const res = await api.post('/bookmarks/folders', { name: newFolderName });
      const created = mapFolder(res.data?.data || res.data);
      setFolders(prev => [...prev, created]);
      setNewFolderName('');
      setFolderDialogOpen(false);
    } catch { /* ignore */ }
  };

  const handleEditBookmark = (bookmarkId: string) => {
    const bookmark = bookmarks.find(b => b.id === bookmarkId);
    if (bookmark) {
      setEditBookmark(bookmark);
      setShowEditDialog(true);
    }
    setMoreMenuAnchor(null);
  };

  const handleSaveEdit = async () => {
    if (!editBookmark) return;
    try {
      const dto = {
        title: editBookmark.title,
        description: editBookmark.description,
        url: editBookmark.url,
      };
      const res = await api.put(`/bookmarks/${editBookmark.id}`, dto);
      const updated = mapBookmark(res.data?.data || res.data);
      setBookmarks(bookmarks.map(b => b.id === editBookmark.id ? updated : b));
      setShowEditDialog(false);
      setEditBookmark(null);
    } catch { /* ignore */ }
  };

  const handleCopyLink = (bookmarkId: string) => {
    const bookmark = bookmarks.find(b => b.id === bookmarkId);
    if (bookmark?.url) {
      navigator.clipboard.writeText(bookmark.url);
      setSnackbar({ open: true, message: 'Link copied to clipboard' });
    }
    setMoreMenuAnchor(null);
  };

  const getBookmarkIcon = (type: Bookmark['type']) => {
    switch (type) {
      case 'email': return <EmailIcon />;
      case 'link': return <LinkIcon />;
      case 'attachment': return <AttachIcon />;
      case 'note': return <BookmarkIcon />;
    }
  };

  const filteredBookmarks = bookmarks
    .filter(b => {
      if (selectedTab === 1) return b.isStarred;
      if (selectedFolder !== 'all') return b.folder === selectedFolder;
      return true;
    })
    .filter(b => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        b.title.toLowerCase().includes(query) ||
        b.description?.toLowerCase().includes(query) ||
        b.tags.some(t => t.toLowerCase().includes(query))
      );
    })
    .sort((a, b) => {
      if (sortBy === 'date') return b.createdAt.getTime() - a.createdAt.getTime();
      if (sortBy === 'name') return a.title.localeCompare(b.title);
      if (sortBy === 'type') return a.type.localeCompare(b.type);
      return 0;
    });

  return (
    <Box sx={{ display: 'flex', height: '100%', bgcolor: '#f5f5f5' }}>
      {/* Sidebar - Folders */}
      <Box
        sx={{
          width: 260,
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={600}>Bookmarks</Typography>
          <Typography variant="body2" color="text.secondary">
            Save and organize items
          </Typography>
        </Box>

        {/* Tabs */}
        <Tabs
          value={selectedTab}
          onChange={(_, v) => setSelectedTab(v)}
          sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All" sx={{ minWidth: 60 }} />
          <Tab label="Starred" sx={{ minWidth: 60 }} />
        </Tabs>

        {/* Folders List */}
        <List sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
          {folders.map(folder => (
            <ListItem key={folder.id} disablePadding>
              <ListItemButton
                selected={selectedFolder === folder.id}
                onClick={() => setSelectedFolder(folder.id)}
              >
                <ListItemAvatar sx={{ minWidth: 40 }}>
                  {selectedFolder === folder.id ? (
                    <FolderOpenIcon sx={{ color: folder.color }} />
                  ) : (
                    <FolderIcon sx={{ color: folder.color }} />
                  )}
                </ListItemAvatar>
                <ListItemText
                  primary={folder.name}
                  secondary={`${folder.count} items`}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Add Folder Button */}
        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button fullWidth variant="outlined" startIcon={<AddIcon />} size="small" onClick={() => setFolderDialogOpen(true)}>
            New Folder
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <TextField
            size="small"
            placeholder="Search bookmarks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Sort by">
            <IconButton onClick={(e) => setSortMenuAnchor(e.currentTarget)}>
              <SortIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={viewMode === 'list' ? 'Grid view' : 'List view'}>
            <IconButton onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}>
              {viewMode === 'list' ? <GridViewIcon /> : <ListViewIcon />}
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddBookmark(true)}
          >
            Add Bookmark
          </Button>
        </Box>

        {/* Bookmarks */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
          {filteredBookmarks.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <BookmarkOutlineIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No bookmarks found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {searchQuery ? 'Try a different search term' : 'Start by adding a bookmark'}
              </Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setShowAddBookmark(true)}>
                Add Bookmark
              </Button>
            </Box>
          ) : viewMode === 'list' ? (
            /* List View */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {filteredBookmarks.map(bookmark => (
                <Card key={bookmark.id} sx={{ '&:hover': { boxShadow: 3 } }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Avatar sx={{ bgcolor: 'primary.light' }}>
                      {getBookmarkIcon(bookmark.type)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" noWrap>
                          {bookmark.title}
                        </Typography>
                        {bookmark.tags.map(tag => (
                          <Chip key={tag} label={tag} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        ))}
                      </Box>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {bookmark.description || bookmark.url || bookmark.subject}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        <TimeIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                        {format(bookmark.createdAt, 'MMM d, yyyy')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip title={bookmark.isStarred ? 'Unstar' : 'Star'}>
                        <IconButton size="small" onClick={() => handleToggleStar(bookmark.id)}>
                          {bookmark.isStarred ? (
                            <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                          ) : (
                            <StarOutlineIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      {bookmark.url && (
                        <Tooltip title="Open link">
                          <IconButton size="small" onClick={() => window.open(bookmark.url, '_blank')}>
                            <OpenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => setMoreMenuAnchor({ anchor: e.currentTarget, bookmarkId: bookmark.id })}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            /* Grid View */
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
              {filteredBookmarks.map(bookmark => (
                <Card key={bookmark.id} sx={{ '&:hover': { boxShadow: 3 } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <Avatar sx={{ bgcolor: 'primary.light', width: 36, height: 36 }}>
                        {getBookmarkIcon(bookmark.type)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }} />
                      <IconButton size="small" onClick={() => handleToggleStar(bookmark.id)}>
                        {bookmark.isStarred ? (
                          <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                        ) : (
                          <StarOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => setMoreMenuAnchor({ anchor: e.currentTarget, bookmarkId: bookmark.id })}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="subtitle2" gutterBottom noWrap>
                      {bookmark.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, height: 40, overflow: 'hidden' }}>
                      {bookmark.description || bookmark.url || bookmark.subject}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                      {bookmark.tags.map(tag => (
                        <Chip key={tag} label={tag} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {format(bookmark.createdAt, 'MMM d, yyyy')}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Sort Menu */}
      <Menu
        anchorEl={sortMenuAnchor}
        open={Boolean(sortMenuAnchor)}
        onClose={() => setSortMenuAnchor(null)}
      >
        <MenuItem onClick={() => { setSortBy('date'); setSortMenuAnchor(null); }}>
          <ListItemText>Date Added</ListItemText>
          {sortBy === 'date' && <Chip label="✓" size="small" />}
        </MenuItem>
        <MenuItem onClick={() => { setSortBy('name'); setSortMenuAnchor(null); }}>
          <ListItemText>Name</ListItemText>
          {sortBy === 'name' && <Chip label="✓" size="small" />}
        </MenuItem>
        <MenuItem onClick={() => { setSortBy('type'); setSortMenuAnchor(null); }}>
          <ListItemText>Type</ListItemText>
          {sortBy === 'type' && <Chip label="✓" size="small" />}
        </MenuItem>
      </Menu>

      {/* Bookmark More Menu */}
      <Menu
        anchorEl={moreMenuAnchor?.anchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleEditBookmark(moreMenuAnchor?.bookmarkId || '')}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleCopyLink(moreMenuAnchor?.bookmarkId || '')}>
          <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Copy Link</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          const bookmark = bookmarks.find(b => b.id === moreMenuAnchor?.bookmarkId);
          if (bookmark?.url) {
            navigator.share?.({ title: bookmark.title, url: bookmark.url }).catch(() => {
              navigator.clipboard.writeText(bookmark.url!);
              setSnackbar({ open: true, message: 'Link copied to clipboard' });
            });
          }
          setMoreMenuAnchor(null);
        }}>
          <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDeleteBookmark(moreMenuAnchor?.bookmarkId || '')} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add Bookmark Dialog */}
      <Dialog open={showAddBookmark} onClose={() => setShowAddBookmark(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Bookmark</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Title"
            value={newBookmark.title}
            onChange={(e) => setNewBookmark({ ...newBookmark, title: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="URL (optional)"
            value={newBookmark.url}
            onChange={(e) => setNewBookmark({ ...newBookmark, url: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={newBookmark.description}
            onChange={(e) => setNewBookmark({ ...newBookmark, description: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddBookmark(false)}>Cancel</Button>
          <Button onClick={handleAddBookmark} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* New Folder Dialog */}
      <Dialog open={folderDialogOpen} onClose={() => setFolderDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Folder Name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateFolder} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Bookmark Dialog */}
      <Dialog open={showEditDialog} onClose={() => { setShowEditDialog(false); setEditBookmark(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Bookmark</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Title"
            value={editBookmark?.title || ''}
            onChange={(e) => setEditBookmark(prev => prev ? { ...prev, title: e.target.value } : null)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="URL"
            value={editBookmark?.url || ''}
            onChange={(e) => setEditBookmark(prev => prev ? { ...prev, url: e.target.value } : null)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={editBookmark?.description || ''}
            onChange={(e) => setEditBookmark(prev => prev ? { ...prev, description: e.target.value } : null)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowEditDialog(false); setEditBookmark(null); }}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default BookmarksPage;
